/**
 * Template Modal
 * Modal for selecting and applying allocation templates
 */
import { useState } from 'react';
import { useAllocationTemplates, useApplyTemplate } from '@/hooks/useAllocation';
import Button from '@/components/ui/Button';
import {
  RISK_PROFILE_DISPLAY_NAMES,
  ASSET_CLASS_DISPLAY_NAMES,
  type AllocationTemplate,
  type AssetClass,
} from '@/types/allocation';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TemplateModal({ isOpen, onClose }: TemplateModalProps) {
  const { data, isLoading } = useAllocationTemplates();
  const { mutate: applyTemplate, isPending } = useApplyTemplate();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [applyPortfolio, setApplyPortfolio] = useState(true);
  const [applyBuckets, setApplyBuckets] = useState(true);

  if (!isOpen) return null;

  const handleApply = () => {
    if (!selectedTemplate) return;
    applyTemplate(
      {
        templateId: selectedTemplate,
        data: { applyPortfolio, applyBuckets },
      },
      {
        onSuccess: () => {
          onClose();
          setSelectedTemplate(null);
        },
      }
    );
  };

  const allTemplates = data
    ? [...data.systemTemplates, ...data.customTemplates]
    : [];

  const selected = allTemplates.find((t) => t.id === selectedTemplate);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Apply Allocation Template
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <TemplateSkeleton />
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Choose a template to set your portfolio and bucket allocation targets.
                </p>

                {/* Template grid */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {allTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      isSelected={selectedTemplate === template.id}
                      onSelect={() => setSelectedTemplate(template.id)}
                    />
                  ))}
                </div>

                {/* Selected template details */}
                {selected && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      {selected.name} - Target Allocations
                    </h3>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* Portfolio targets - handle both old 'targets' and new 'portfolioTargets' field */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                          Portfolio (by Asset Class)
                        </h4>
                        <div className="space-y-1">
                          {Object.entries(selected.portfolioTargets ?? selected.targets ?? {})
                            .filter(([, pct]) => pct && parseFloat(String(pct)) > 0)
                            .sort(([, a], [, b]) => parseFloat(String(b)) - parseFloat(String(a)))
                            .map(([assetClass, pct]) => (
                              <div
                                key={assetClass}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-600">
                                  {ASSET_CLASS_DISPLAY_NAMES[assetClass as AssetClass] ?? assetClass}
                                </span>
                                <span className="font-medium text-gray-900">
                                  {parseFloat(String(pct)).toFixed(0)}%
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* Bucket targets - only show if present */}
                      {selected.bucketTargets && Object.keys(selected.bucketTargets).length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                            Buckets (by Strategic Role)
                          </h4>
                          <div className="space-y-1">
                            {Object.entries(selected.bucketTargets)
                              .filter(([, pct]) => pct && parseFloat(String(pct)) > 0)
                              .sort(([, a], [, b]) => parseFloat(String(b)) - parseFloat(String(a)))
                              .map(([role, pct]) => (
                                <div
                                  key={role}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-gray-600">{role}</span>
                                  <span className="font-medium text-gray-900">
                                    {parseFloat(String(pct)).toFixed(0)}%
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Apply options */}
                    <div className="mt-4 flex items-center gap-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={applyPortfolio}
                          onChange={(e) => setApplyPortfolio(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Apply portfolio targets
                        </span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={applyBuckets}
                          onChange={(e) => setApplyBuckets(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Apply bucket targets
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              disabled={!selectedTemplate || (!applyPortfolio && !applyBuckets)}
              isLoading={isPending}
            >
              Apply Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface TemplateCardProps {
  template: AllocationTemplate;
  isSelected: boolean;
  onSelect: () => void;
}

function TemplateCard({ template, isSelected, onSelect }: TemplateCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`
        w-full text-left p-4 rounded-lg border-2 transition-colors
        ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        }
      `}
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
          <p className="mt-1 text-xs text-gray-500">{template.description}</p>
        </div>
        {template.isSystem && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            System
          </span>
        )}
      </div>
      <div className="mt-2">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {RISK_PROFILE_DISPLAY_NAMES[template.riskProfile]}
        </span>
      </div>
    </button>
  );
}

function TemplateSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
